import React, { useState, useEffect } from 'react';
import { adminAPI} from '../../services/api';
import { useAuth } from '../../hooks/useAuth';
import './AdminDashboard.css';
import { 
  Users, 
  Clock, 
  Monitor, 
  SmartphoneNfc, 
  Edit3,
  Calendar,
  Heart,
  Palmtree,
  Gift,
  Home,
  AlertCircle,
  CheckCircle,
  RefreshCw,
  Briefcase,
  Bolt
} from 'lucide-react';

interface ActiveUser {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  currentWorkLog: {
    id: number;
    startTime: string;
    endTime?: string;
    description: string | null;
    logMethod: string;
    elapsedMinutes: number;
    elapsedHours: number;
  };
  maxWorkHours: number;
  isNearLimit: boolean;
  isOverLimit: boolean;
  status: 'currently_working' | 'worked_today';
  isWorkDay?: boolean; // Neu hinzugefügt
  workType?: 'regular' | 'overtime'; // Neu hinzugefügt
}

interface OffUser {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  reason: 'vacation' | 'sick' | 'special_leave' | 'personal' | 'other' | 'holiday' | 'weekend' | 'not_started';
  reasonDetails?: string;
  holidayName?: string;
  category: 'time_off' | 'holiday' | 'non_working_day' | 'should_work_not_started';
}

interface DashboardStats {
  totalUsers: number;
  activeUsers: number;
  usersOnVacation: number;
  usersOnSick: number;
  usersOvertime: number;
  legitimatelyOff: number;
  shouldWorkNotStarted: number;
}

export const AdminDashboard: React.FC = () => {
  const { user } = useAuth();
  const [activeUsers, setActiveUsers] = useState<ActiveUser[]>([]);
  const [legitimatelyOffUsers, setLegitimatelyOffUsers] = useState<OffUser[]>([]);
  const [notYetWorkingUsers, setNotYetWorkingUsers] = useState<OffUser[]>([]);
  const [stats, setStats] = useState<DashboardStats>({
    totalUsers: 0,
    activeUsers: 0,
    usersOnVacation: 0,
    usersOnSick: 0,
    usersOvertime: 0,
    legitimatelyOff: 0,
    shouldWorkNotStarted: 0
  });
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());
  const [autoRefresh, setAutoRefresh] = useState(true);

  useEffect(() => {
    loadDashboardData();
    
    // Auto-refresh every 30 seconds
    const interval = setInterval(() => {
      if (autoRefresh) {
        loadDashboardData();
      }
    }, 30000);

    return () => clearInterval(interval);
  }, [autoRefresh]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      
      // Load active working users
      const activeResponse = await adminAPI.getActiveWorkingUsers();
      setActiveUsers(activeResponse.data.activeUsers);
      
      // Load users who are off today  
      const offResponse = await adminAPI.getUsersOffToday();
      
      console.log('Dashboard API Response:', offResponse.data); // Debug log
      
      // Separate the different types of off users
      const allOffUsers = offResponse.data.offUsers || [];
      const legitimatelyOff = offResponse.data.legitimatelyOff || [];
      const notYetWorking = offResponse.data.notYetWorking || [];
      
      console.log('Legitimately off users:', legitimatelyOff); // Debug log
      console.log('Not yet working users:', notYetWorking); // Debug log
      
      //setOffUsers(allOffUsers);
      setLegitimatelyOffUsers(legitimatelyOff);
      setNotYetWorkingUsers(notYetWorking);
      
      
      setStats({
        totalUsers: activeResponse.data.activeUsers.length + allOffUsers.length,
        activeUsers: activeResponse.data.activeUsers.length,
        usersOnVacation: legitimatelyOff.filter((u: OffUser) => u.reason === 'vacation').length,
        usersOnSick: legitimatelyOff.filter((u: OffUser) => u.reason === 'sick').length,
        usersOvertime: activeResponse.data.activeUsers.filter((u: ActiveUser) => u.isOverLimit).length,
        legitimatelyOff: legitimatelyOff.length,
        shouldWorkNotStarted: notYetWorking.length
      });
      
      setLastRefresh(new Date());
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatElapsedTime = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}:${mins.toString().padStart(2, '0')}`;
  };

  const getMethodIcon = (method: string) => {
    switch (method) {
      case 'WEBSITE':
        return <Monitor size={16} className="method-icon website" />;
      case 'NFC':
        return <SmartphoneNfc size={16} className="method-icon nfc" />;
      case 'MANUAL_ADMIN':
        return <Edit3 size={16} className="method-icon manual" />;
      default:
        return <Clock size={16} className="method-icon default" />;
    }
  };

  const getMethodLabel = (method: string) => {
    switch (method) {
      case 'WEBSITE':
        return 'Website';
      case 'NFC':
        return 'NFC Karte';
      case 'MANUAL_ADMIN':
        return 'Manuell';
      default:
        return method;
    }
  };

  const getReasonIcon = (reason: string) => {
    switch (reason) {
      case 'vacation':
        return <Palmtree size={16} className="reason-icon vacation" />;
      case 'sick':
        return <Heart size={16} className="reason-icon sick" />;
      case 'special_leave':
        return <Gift size={16} className="reason-icon special" />;
      case 'personal':
        return <Calendar size={16} className="reason-icon personal" />;
      case 'other':
        return <Calendar size={16} className="reason-icon other" />;
      case 'holiday':
        return <Calendar size={16} className="reason-icon holiday" />;
      case 'weekend':
        return <Home size={16} className="reason-icon weekend" />;
      default:
        return <Calendar size={16} className="reason-icon default" />;
    }
  };

  const getReasonLabel = (reason: string) => {
    switch (reason) {
      case 'vacation':
        return 'Urlaub';
      case 'sick':
        return 'Krank';
      case 'special_leave':
        return 'Sonderurlaub';
      case 'personal':
        return 'Persönlicher Urlaub';
      case 'other':
        return 'Sonstiges';
      case 'holiday':
        return 'Feiertag';
      case 'weekend':
        return 'Kein Arbeitstag';
      case 'not_started':
        return 'Noch nicht eingestempelt';
      default:
        return reason;
    }
  };

  if (!user || !['ADMIN', 'MANAGER'].includes(user.role)) {
    return (
      <div className="access-denied">
        <AlertCircle size={48} />
        <h2>Zugriff verweigert</h2>
        <p>Sie haben keine Berechtigung für das Admin Dashboard.</p>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      <div className="dashboard-header">
        <div className="header-info">
          <h1>
            <Users size={32} />
            Admin Dashboard
          </h1>
          <p className="header-subtitle">
            Übersicht aller Mitarbeiter und aktuellen Arbeitszeiten
          </p>
        </div>
        
        <div className="header-controls">
          <div className="auto-refresh">
            <label>
              <input
                type="checkbox"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
              />
              Auto-Refresh (30s)
            </label>
          </div>
          <button 
            className="btn secondary refresh-btn" 
            onClick={loadDashboardData}
            disabled={loading}
          >
            <RefreshCw size={16} className={loading ? 'spinning' : ''} />
            Aktualisieren
          </button>
          <div className="last-refresh">
            Letzte Aktualisierung: {lastRefresh.toLocaleTimeString('de-DE')}
          </div>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="dashboard-stats">
        <div className="stat-card total">
          <div className="stat-icon">
            <Users size={24} />
          </div>
          <div className="stat-content">
            <h3>{stats.totalUsers}</h3>
            <p>Gesamt Mitarbeiter</p>
          </div>
        </div>

        <div className="stat-card active">
          <div className="stat-icon">
            <CheckCircle size={24} />
          </div>
          <div className="stat-content">
            <h3>{stats.activeUsers}</h3>
            <p>Aktiv arbeitend</p>
          </div>
        </div>

        <div className="stat-card vacation">
          <div className="stat-icon">
            <Palmtree size={24} />
          </div>
          <div className="stat-content">
            <h3>{stats.usersOnVacation}</h3>
            <p>Im Urlaub</p>
          </div>
        </div>

        <div className="stat-card sick">
          <div className="stat-icon">
            <Heart size={24} />
          </div>
          <div className="stat-content">
            <h3>{stats.usersOnSick}</h3>
            <p>Krank</p>
          </div>
        </div>

        <div className="stat-card legitimately-off">
          <div className="stat-icon">
            <Home size={24} />
          </div>
          <div className="stat-content">
            <h3>{stats.legitimatelyOff}</h3>
            <p>Berechtigt abwesend</p>
          </div>
        </div>

        <div className="stat-card should-work">
          <div className="stat-icon">
            <AlertCircle size={24} />
          </div>
          <div className="stat-content">
            <h3>{stats.shouldWorkNotStarted}</h3>
            <p>Noch nicht da</p>
          </div>
        </div>
      </div>

      {/* Active Users Section */}
      <div className="dashboard-section">
        <div className="section-header">
          <h2>
            <CheckCircle size={24} />
            Aktive Mitarbeiter heute ({activeUsers.length})
          </h2>
          <p className="section-subtitle">
            Alle Mitarbeiter mit Arbeitsaktivität heute (inkl. Überstunden an Nicht-Arbeitstagen)
          </p>
        </div>
        
        {loading ? (
          <div className="loading">Lade aktive Mitarbeiter...</div>
        ) : activeUsers.length === 0 ? (
          <div className="empty-state">
            <Clock size={48} />
            <h3>Keine Arbeitsaktivität</h3>
            <p>Heute hat noch niemand gearbeitet.</p>
          </div>
        ) : (
          <div className="users-grid">
            {activeUsers.map((user) => (
              <div 
                key={user.id} 
                className={`user-card active ${user.status === 'currently_working' ? (user.isOverLimit ? 'overtime' : user.isNearLimit ? 'near-limit' : '') : 'worked-today'} ${user.workType === 'overtime' ? 'non-workday' : ''}`}
              >
                <div className="user-header">
                  <div className="user-info">
                    <h3>{user.firstName} {user.lastName}</h3>
                    <p className="user-email">{user.email}</p>
                    {user.workType === 'overtime' && (
                      <p className="workday-indicator overtime">
                        <Bolt /> Überstunden (Nicht-Arbeitstag)
                      </p>
                    )}
                    {user.workType === 'regular' && (
                      <p className="workday-indicator regular">
                        <Briefcase /> Regulärer Arbeitstag
                      </p>
                    )}
                  </div>
                  <div className="user-status">
                    {user.status === 'currently_working' && (
                      <>
                        {user.isOverLimit && (
                          <span className="status-badge overtime">
                            <AlertCircle size={14} />
                            Überstunden
                          </span>
                        )}
                        {user.isNearLimit && !user.isOverLimit && (
                          <span className="status-badge near-limit">
                            <AlertCircle size={14} />
                            Nah am Limit
                          </span>
                        )}
                        {!user.isNearLimit && !user.isOverLimit && (
                          <span className="status-badge working">
                            <CheckCircle size={14} />
                            Arbeitet
                          </span>
                        )}
                      </>
                    )}
                    {user.status === 'worked_today' && (
                      <>
                        <span className="status-badge completed">
                          <CheckCircle size={14} />
                          Beendet
                        </span>
                      </>
                    )}
                  </div>
                </div>
                
                <div className="work-info">
                  <div className="time-display">
                    <Clock size={20} />
                    <span className="elapsed-time">
                      {formatElapsedTime(user.currentWorkLog.elapsedMinutes)}
                    </span>
                    {user.status === 'currently_working' && user.workType === 'regular' && (
                      <span className="max-time">
                        / {user.maxWorkHours}h
                      </span>
                    )}
                  </div>
                  
                  <div className="start-info">
                    <span className="start-time">
                      Gestartet: {new Date(user.currentWorkLog.startTime).toLocaleTimeString('de-DE', {
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                      {user.currentWorkLog.endTime && (
                        <span> - Beendet: {new Date(user.currentWorkLog.endTime).toLocaleTimeString('de-DE', {
                          hour: '2-digit',
                          minute: '2-digit'
                        })}</span>
                      )}
                    </span>
                  </div>
                </div>
                
                <div className="method-info">
                  <div className="method-display">
                    {getMethodIcon(user.currentWorkLog.logMethod)}
                    <span>{getMethodLabel(user.currentWorkLog.logMethod)}</span>
                  </div>
                </div>
                
                {user.currentWorkLog.description && (
                  <div className="description">
                    <p>"{user.currentWorkLog.description}"</p>
                  </div>
                )}
                
                {user.status === 'currently_working' && user.workType === 'regular' && (
                  <div className="progress-bar">
                    <div 
                      className={`progress-fill ${user.isOverLimit ? 'overtime' : user.isNearLimit ? 'near-limit' : ''}`}
                      style={{
                        width: `${Math.min(100, (user.currentWorkLog.elapsedHours / user.maxWorkHours) * 100)}%`
                      }}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
            {/* Should Work But Not Started Section */}
      <div className="dashboard-section">
        <div className="section-header">
          <h2>
            <AlertCircle size={24} />
            Abwesend ({notYetWorkingUsers.length})
          </h2>
          <p className="section-subtitle">
            Mitarbeiter mit regulärem Arbeitstag, die noch nicht eingestempelt sind
          </p>
        </div>
        
        {loading ? (
          <div className="loading">Lade nicht gestartete Mitarbeiter...</div>
        ) : notYetWorkingUsers.length === 0 ? (
          <div className="empty-state">
            <CheckCircle size={48} />
            <h3>Alle da oder berechtigt abwesend</h3>
            <p>Alle Mitarbeiter mit Arbeitstag haben bereits gearbeitet oder sind berechtigt abwesend.</p>
          </div>
        ) : (
          <div className="users-grid off-users should-work">
            {notYetWorkingUsers.map((user) => (
              <div key={user.id} className={`user-card off ${user.reason} should-work-missing`}>
                <div className="user-header">
                  <div className="user-info">
                    <h3>{user.firstName} {user.lastName}</h3>
                    <p className="user-email">{user.email}</p>
                  </div>
                  <div className="missing-indicator">
                    <AlertCircle size={16} />
                    <span>Fehlt</span>
                  </div>
                </div>
                
                <div className="off-reason">
                  <div className="reason-display">
                    <Clock size={16} className="reason-icon missing" />
                    <span>{getReasonLabel(user.reason)}</span>
                  </div>
                  
                  {user.reasonDetails && (
                    <div className="reason-details">
                      <p>"{user.reasonDetails}"</p>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Legitimately Off Users Section */}
      <div className="dashboard-section">
        <div className="section-header">
          <h2>
            <Home size={24} />
            Berechtigt abwesende Mitarbeiter ({legitimatelyOffUsers.length})
          </h2>
          <p className="section-subtitle">
            Mitarbeiter mit Urlaub, Krankmeldung, Feiertag oder nicht Arbeitstag
          </p>
        </div>
        
        {loading ? (
          <div className="loading">Lade abwesende Mitarbeiter...</div>
        ) : legitimatelyOffUsers.length === 0 ? (
          <div className="empty-state">
            <Users size={48} />
            <h3>Keine berechtigten Abwesenheiten</h3>
            <p>Alle Mitarbeiter haben heute regulären Arbeitstag.</p>
          </div>
        ) : (
          <div className="users-grid off-users">
            {legitimatelyOffUsers.map((user) => (
              <div key={user.id} className={`user-card off ${user.reason} legitimate-off`}>
                <div className="user-header">
                  <div className="user-info">
                    <h3>{user.firstName} {user.lastName}</h3>
                    <p className="user-email">{user.email}</p>
                  </div>
                </div>
                
                <div className="off-reason">
                  <div className="reason-display">
                    {getReasonIcon(user.reason)}
                    <span>{getReasonLabel(user.reason)}</span>
                  </div>
                  
                  {user.holidayName && (
                    <div className="reason-details">
                      <p>{user.holidayName}</p>
                    </div>
                  )}
                  
                  {user.reasonDetails && (
                    <div className="reason-details">
                      <p>"{user.reasonDetails}"</p>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
